import { useCallback, useRef } from 'react';

/**
 * Returns a debounced version of the callback that prevents rapid successive calls.
 * Perfect for Run buttons to prevent double-click spam.
 *
 * @param {Function} callback - The function to debounce
 * @param {number} delay - Delay in ms (default 300)
 * @returns {Function} - Debounced function
 */
export function useDebounce(callback, delay = 300) {
  const timeoutRef = useRef(null);
  const isBlockedRef = useRef(false);

  return useCallback((...args) => {
    // If already blocked (recently called), ignore
    if (isBlockedRef.current) {
      return;
    }

    // Block subsequent calls
    isBlockedRef.current = true;

    // Execute immediately
    callback(...args);

    // Unblock after delay
    timeoutRef.current = setTimeout(() => {
      isBlockedRef.current = false;
    }, delay);
  }, [callback, delay]);
}

export default useDebounce;
